import { useEffect, useMemo, useState } from "react";
import { Plus, Trash2, UserCircle } from "lucide-react";
import { useTenant } from "@/lib/tenant-context.js";
import { useAsync } from "@/hooks/use-async.js";
import { Button } from "@/components/ui/button.js";
import { Input } from "@/components/ui/input.js";
import { Label } from "@/components/ui/label.js";
import { Select } from "@/components/ui/select.js";
import { Skeleton } from "@/components/ui/skeleton.js";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card.js";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog.js";
import { useToast } from "@/components/ui/toast.js";
import { formatTime, isoDate } from "@/lib/format.js";
import { getMyResourceId, setMyResourceId } from "@/lib/me-identity.js";
import type { Appointment, Schedule } from "@/lib/types.js";

/**
 * Personal staff page. Mobile-first: identity card → today's appointments →
 * personal time off. Identity is stored client-side (localStorage) until the
 * backend adds a staff_user → resource_id link. Time off is a thin UI over
 * `PUT /api/v1/resources/:id/schedule`'s `time_off[]` array.
 */
export function MePage(): React.JSX.Element {
  const { api, tenantSlug } = useTenant();
  const resources = useAsync(() => api.listResources(), [api]);
  const [resourceId, setResourceIdState] = useState<string | null>(() => getMyResourceId(tenantSlug));

  const visibleResources = (resources.data?.data ?? []).filter((r) => !r.deleted_at);
  const me = useMemo(
    () => visibleResources.find((r) => r.id === resourceId) ?? null,
    [visibleResources, resourceId]
  );

  function pickResource(id: string | null): void {
    setMyResourceId(tenantSlug, id);
    setResourceIdState(id);
  }

  return (
    <div className="flex flex-col gap-4">
      <div>
        <div className="text-xs uppercase tracking-wider text-muted-foreground">My account</div>
        <h1 className="text-xl font-semibold">{me ? me.name : "Pick your name"}</h1>
        <p className="text-sm text-muted-foreground">
          {me
            ? "Your day, your time off, all in one place."
            : "Pick which staff record is you to see your schedule and add time off."}
        </p>
      </div>

      <IdentityCard
        resources={visibleResources}
        resourceId={resourceId}
        onChange={pickResource}
      />

      {me ? (
        <>
          <TodayCard resourceId={me.id} />
          <TimeOffCard resourceId={me.id} />
        </>
      ) : (
        <div className="rounded-lg border border-dashed p-6 text-center text-sm text-muted-foreground">
          Pick yourself above to see your day.
        </div>
      )}
    </div>
  );
}

function IdentityCard({
  resources,
  resourceId,
  onChange
}: {
  resources: Array<{ id: string; name: string }>;
  resourceId: string | null;
  onChange: (id: string | null) => void;
}): React.JSX.Element {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-sm">
          <UserCircle className="h-4 w-4" />
          Identity
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="me-resource">I am</Label>
          <Select
            id="me-resource"
            value={resourceId ?? ""}
            onChange={(e) => onChange(e.target.value || null)}
          >
            <option value="">— pick one —</option>
            {resources.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
              </option>
            ))}
          </Select>
          <p className="text-[11px] text-muted-foreground">
            Saved on this device. Real auth will come from your account once
            the staff-user → resource link ships.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}

function TodayCard({ resourceId }: { resourceId: string }): React.JSX.Element {
  const { api } = useTenant();
  const today = useMemo(() => {
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    const end = new Date();
    end.setHours(23, 59, 59, 999);
    return { from: start.toISOString(), to: end.toISOString() };
  }, []);
  const appointments = useAsync(
    () => api.listAppointments({ from: today.from, to: today.to }),
    [api, today.from, today.to]
  );
  const services = useAsync(() => api.listServices(), [api]);
  const customers = useAsync(() => api.listCustomers(), [api]);
  const resources = useAsync(() => api.listResources(), [api]);

  const mine = (appointments.data?.data ?? [])
    .filter((a) => a.primary_resource_id === resourceId)
    .sort((a, b) => Date.parse(a.starts_at) - Date.parse(b.starts_at));
  const serviceById = new Map((services.data?.data ?? []).map((s) => [s.id, s]));
  const customerById = new Map((customers.data?.data ?? []).map((c) => [c.id, c]));
  const resourceById = new Map((resources.data?.data ?? []).map((r) => [r.id, r]));

  const dateLabel = new Date().toLocaleDateString(undefined, {
    weekday: "long",
    month: "short",
    day: "numeric"
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between text-sm">
          <span>Today — {dateLabel}</span>
          <span className="text-xs text-muted-foreground tabular-nums">
            {mine.length} {mine.length === 1 ? "appt" : "appts"}
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {appointments.loading ? (
          <Skeleton className="h-24 w-full" />
        ) : mine.length === 0 ? (
          <div className="py-6 text-center text-sm text-muted-foreground">
            Nothing booked. Take a breath.
          </div>
        ) : (
          <ul className="flex flex-col gap-1.5">
            {mine.map((appointment) => (
              <AppointmentRow
                key={appointment.id}
                appointment={appointment}
                serviceName={serviceById.get(appointment.service_id)?.name ?? "Appointment"}
                customerName={appointment.customer_name ?? customerById.get(appointment.customer_id)?.name ?? "—"}
                resourceName={
                  appointment.resource_name ??
                  resourceById.get(appointment.primary_resource_id)?.name
                }
              />
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function AppointmentRow({
  appointment,
  serviceName,
  customerName,
  resourceName
}: {
  appointment: Appointment;
  serviceName: string;
  customerName: string;
  resourceName?: string;
}): React.JSX.Element {
  const cancelled = appointment.lifecycle_state === "cancelled";
  return (
    <li
      className={`flex items-center gap-3 rounded-md border bg-background/60 px-3 py-2 text-sm ${
        cancelled ? "opacity-60 line-through" : ""
      }`}
    >
      <div className="flex w-16 flex-col text-xs tabular-nums">
        <span className="font-semibold text-foreground">{formatTime(appointment.starts_at)}</span>
        <span className="text-muted-foreground">{formatTime(appointment.ends_at)}</span>
      </div>
      <div className="min-w-0 flex-1">
        <div className="truncate font-medium">{serviceName}</div>
        <div className="truncate text-xs text-muted-foreground">
          {customerName}
          {resourceName && (
            <>
              {" · "}
              <span data-testid={`me-resource-${appointment.id}`}>{resourceName}</span>
            </>
          )}
        </div>
      </div>
    </li>
  );
}

function TimeOffCard({ resourceId }: { resourceId: string }): React.JSX.Element {
  const { api } = useTenant();
  const { toast } = useToast();
  const schedule = useAsync(() => api.getSchedule(resourceId), [api, resourceId]);
  const [adding, setAdding] = useState(false);
  const [pendingId, setPendingId] = useState<number | null>(null);

  const rows = schedule.data?.time_off ?? [];

  async function remove(index: number): Promise<void> {
    if (!schedule.data) return;
    setPendingId(index);
    try {
      const next: Schedule = {
        ...schedule.data,
        time_off: schedule.data.time_off.filter((_, i) => i !== index)
      };
      await api.putSchedule(resourceId, next);
      toast({ title: "Time off removed", variant: "success" });
      schedule.reload();
    } catch (error) {
      toast({ title: "Could not remove", description: String(error), variant: "destructive" });
    } finally {
      setPendingId(null);
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between text-sm">
          <span>My time off</span>
          <Button size="sm" onClick={() => setAdding(true)}>
            <Plus className="h-4 w-4" />
            Add
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {schedule.loading ? (
          <Skeleton className="h-24 w-full" />
        ) : rows.length === 0 ? (
          <div className="py-6 text-center text-sm text-muted-foreground">
            No time off scheduled.
          </div>
        ) : (
          <ul className="flex flex-col gap-1.5">
            {rows.map((row, index) => (
              <li
                key={`${row.starts_at}-${index}`}
                className="flex items-center gap-3 rounded-md border bg-background/60 px-3 py-2 text-sm"
              >
                <div className="min-w-0 flex-1">
                  <div className="truncate font-medium">{row.reason || "Time off"}</div>
                  <div className="truncate text-xs text-muted-foreground tabular-nums">
                    {formatRange(row.starts_at, row.ends_at)}
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="icon-sm"
                  aria-label="Remove"
                  onClick={() => remove(index)}
                  disabled={pendingId === index}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
      <AddTimeOffDialog
        open={adding}
        onOpenChange={setAdding}
        existing={schedule.data}
        resourceId={resourceId}
        onSaved={() => {
          schedule.reload();
          setAdding(false);
        }}
      />
    </Card>
  );
}

function AddTimeOffDialog({
  open,
  onOpenChange,
  existing,
  resourceId,
  onSaved
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  existing: Schedule | undefined;
  resourceId: string;
  onSaved: () => void;
}): React.JSX.Element {
  const { api } = useTenant();
  const { toast } = useToast();
  const [submitting, setSubmitting] = useState(false);
  const [allDay, setAllDay] = useState(true);
  const [date, setDate] = useState(() => isoDate(new Date()));
  const [endDate, setEndDate] = useState(() => isoDate(new Date()));
  const [from, setFrom] = useState("09:00");
  const [to, setTo] = useState("17:00");
  const [reason, setReason] = useState("");

  // Reset state on open so the dialog doesn't show stale values from a
  // previous session.
  useEffect(() => {
    if (!open) return;
    const today = isoDate(new Date());
    setAllDay(true);
    setDate(today);
    setEndDate(today);
    setFrom("09:00");
    setTo("17:00");
    setReason("");
  }, [open]);

  async function submit(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    if (!existing) return;
    if (!date) {
      toast({ title: "Pick a date", variant: "destructive" });
      return;
    }
    const fromIso = allDay ? `${date}T00:00:00` : `${date}T${from}:00`;
    const toIso = allDay ? `${endDate || date}T23:59:59` : `${endDate || date}T${to}:00`;
    const fromMs = Date.parse(fromIso);
    const toMs = Date.parse(toIso);
    if (Number.isNaN(fromMs) || Number.isNaN(toMs) || toMs <= fromMs) {
      toast({ title: "End must be after start", variant: "destructive" });
      return;
    }
    setSubmitting(true);
    try {
      const next: Schedule = {
        ...existing,
        time_off: [
          ...existing.time_off,
          { starts_at: new Date(fromMs).toISOString(), ends_at: new Date(toMs).toISOString(), reason: reason || "" }
        ]
      };
      await api.putSchedule(resourceId, next);
      toast({ title: "Time off added", variant: "success" });
      onSaved();
    } catch (error) {
      toast({ title: "Could not save", description: String(error), variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add time off</DialogTitle>
        </DialogHeader>
        <form className="grid gap-3" onSubmit={submit}>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={allDay}
              onChange={(e) => setAllDay(e.target.checked)}
              className="h-4 w-4"
            />
            <span>All day</span>
          </label>

          <div className="grid grid-cols-2 gap-2">
            <div className="flex flex-col gap-1">
              <Label htmlFor="me-time-off-date">Start</Label>
              <Input
                id="me-time-off-date"
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                required
              />
            </div>
            <div className="flex flex-col gap-1">
              <Label htmlFor="me-time-off-end">End</Label>
              <Input
                id="me-time-off-end"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                min={date}
              />
            </div>
          </div>

          {!allDay && (
            <div className="grid grid-cols-2 gap-2">
              <div className="flex flex-col gap-1">
                <Label htmlFor="me-time-off-from">From</Label>
                <Input
                  id="me-time-off-from"
                  type="time"
                  value={from}
                  onChange={(e) => setFrom(e.target.value)}
                />
              </div>
              <div className="flex flex-col gap-1">
                <Label htmlFor="me-time-off-to">To</Label>
                <Input
                  id="me-time-off-to"
                  type="time"
                  value={to}
                  onChange={(e) => setTo(e.target.value)}
                />
              </div>
            </div>
          )}

          <div className="flex flex-col gap-1">
            <Label htmlFor="me-time-off-reason">Reason (optional)</Label>
            <Input
              id="me-time-off-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              maxLength={120}
              placeholder="Vacation, sick, training…"
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? "Saving…" : "Add"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function formatRange(fromIso: string, toIso: string): string {
  const from = new Date(fromIso);
  const to = new Date(toIso);
  if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) {
    return `${fromIso} → ${toIso}`;
  }
  const sameDay = isoDate(from) === isoDate(to);
  const dateOpts: Intl.DateTimeFormatOptions = { month: "short", day: "numeric" };
  const timeOpts: Intl.DateTimeFormatOptions = { hour: "numeric", minute: "2-digit" };
  if (sameDay) {
    return `${from.toLocaleDateString(undefined, dateOpts)} · ${from.toLocaleTimeString(undefined, timeOpts)}–${to.toLocaleTimeString(undefined, timeOpts)}`;
  }
  return `${from.toLocaleDateString(undefined, dateOpts)} → ${to.toLocaleDateString(undefined, dateOpts)}`;
}
